import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { FormViewParams, StateModel } from '../redux/models/state.model';
import {
  clearDialogsStack,
  closeDialog,
  openDialog,
  setDialogContext,
  setDialogViewData,
} from '../redux/reducers/dialogViewsReducer';
import {
  clearSuspendedPopovers,
  closePageView,
  NavigationType,
  openPageView as openView, setPageContext,
  setPageViewData,
  setupViewStack,
  suspendPopovers,
} from '../redux/reducers/pageViewsReducer';
import { ViewParams, viewParamsToString } from '../models/ViewParams';
import { useAppDispatch } from '../redux/store';
import { FormContextValue } from '../contexts/FormContext';
import { PageAreaBreadcrumbs } from '../components/BreadcrumbsComponent/models/PageAreaBreadcrumbs';
import { Breadcrumb } from '../components/BreadcrumbsComponent/models/Breadcrumb';
import {
  clearSidebarStack,
  closeSidebarView,
  openSidebar,
  setSidebarContext,
  setSidebarViewData,
} from '../redux/reducers/sidebarViewsReduser';
import { dispatchByActiveView } from '../utils/dispatchByActiveView';
import { cloneViewStack, createFormViewParams, openNewWindow } from '../utils/navigation';
import { v4 as uuid } from 'uuid';
import {
  closeWindowView,
  openWindowView,
  setWindowViewData,
} from '../redux/reducers/WindowViewStateReducer';

interface OpenPageViewOptions {
  viewParams: ViewParams;
  crumb: Breadcrumb;
  openInNewWindow?: boolean;
  shouldLoadData?: boolean;
}

const useViewController = () => {
  const appDispatch = useAppDispatch();

  const dialogs = useSelector((state: StateModel) => state.dialogViewsState);
  const sidebars = useSelector((state: StateModel) => state.sidebarViewsState);
  const viewStack = useSelector((state: StateModel) => state.pageViewStack);
  const windowView = useSelector((state: StateModel) => state.windowViewState);

  const [currentView, setCurrentView] = useState<FormViewParams>(null);

  useEffect(() => {
    if (viewStack && viewStack.length) setCurrentView(viewStack[viewStack.length - 1]);
  }, [viewStack]);

  useEffect(() => {
    if (windowView?.viewParams) {
      appDispatch(closeWindowView());
    }
  }, [viewStack, dialogs, sidebars]);

  useEffect(() => {
    if (currentView?.suspendedPopovers) {
      const { sidebars: suspendedSidebars, dialogs: suspendedDialogs } =
        currentView.suspendedPopovers;

      if (suspendedSidebars && suspendedSidebars.length) {
        suspendedSidebars.forEach((s) => appDispatch(openSidebar(s)));
      }

      if (suspendedDialogs && suspendedDialogs.length) {
        suspendedDialogs.forEach((d) => appDispatch(openDialog(d)));
      }

      appDispatch(clearSuspendedPopovers());
    }
  }, [currentView, appDispatch]);

  /** Функция для открытия вьюхи в диалоге */
  const openModalView = useCallback(
    (viewParams: ViewParams) =>
      appDispatch(
        openDialog({
          viewRequest: viewParams,
          id: uuid(),
        }),
      ),
    [appDispatch],
  );

  /** Функция для открытия сайдбара */
  const openSidebarView = useCallback(
    (viewParams: ViewParams, sidebarAppearPosition: string) =>
      appDispatch(
        openSidebar({
          viewRequest: viewParams,
          sidebarAppearPosition,
        }),
      ),
    [appDispatch],
  );

  /** Универсальный для плавающего окна, модалки, фулпейдж и сайдбаров диспатчер данных для запроса pageData.
   * Сначала данные для Dialog, потом для Sidebar, потом для модалок **/
  const setDataRequest = useCallback(
    (dataRequest: ViewParams) =>
      appDispatch((dispatch, getState) =>
        dispatchByActiveView(getState, {
          window: () => dispatch(setWindowViewData(dataRequest)),
          dialog: () => dispatch(setDialogViewData(dataRequest)),
          sidebar: () => dispatch(setSidebarViewData(dataRequest)),
          page: () => dispatch(setPageViewData(dataRequest)),
        }),
      ),
    [appDispatch],
  );

  /** Функция для устрановки контекста диалога/сайдбара */
  const setModalContext = useCallback(
    (context: FormContextValue) =>
      appDispatch((dispatch, getState) =>
        dispatchByActiveView(getState, {
          dialog: () => dispatch(setDialogContext(context)),
          sidebar: () => dispatch(setSidebarContext(context)),
          page: () => dispatch(setPageContext(context)),
        }),
      ),
    [appDispatch],
  );

  /**
   * Функция для открытия вьюхи в full-page варианте
   * @param options - объект с параметрами открытия
   */
  const openPageView = useCallback(
    (options: OpenPageViewOptions) => {
      const { viewParams, crumb, openInNewWindow = false, shouldLoadData } = options;

      const newView = createFormViewParams(viewParams, crumb, shouldLoadData ? viewParams : null);
      if (openInNewWindow) {
        const newViewStack = [...cloneViewStack(viewStack), newView];
        openNewWindow(newViewStack);
        return;
      }

      if (sidebars.length || dialogs.length) {
        appDispatch(
          suspendPopovers({
            sidebars: [...sidebars],
            dialogs: [...dialogs],
          }),
        );
      }

      appDispatch(
        openView({
          viewRequest: viewParams,
          breadcrumbs: { [viewParamsToString(viewParams)]: [crumb] },
        }),
      );

      if (dialogs.length) {
        appDispatch(clearDialogsStack());
      }
      if (sidebars.length) {
        appDispatch(clearSidebarStack());
      }
      if (windowView?.viewParams) {
        appDispatch(closeWindowView());
      }
    },
    [appDispatch, dialogs, sidebars, viewStack],
  );

  /** Функция закрытия сайдбара */
  const closeSidebar = useCallback(() => appDispatch(closeSidebarView()), [appDispatch]);

  /** Функция закрытия вьюхи (общий стек: [...fullPageViews, ...dialogViews]) */
  const closeView = useCallback(
    () =>
      appDispatch((dispatch, getState) => {
        const state = getState();
        if (state.windowViewState?.viewParams) {
          dispatch(closeWindowView());
        }
        dispatchByActiveView(getState, {
          dialog: () => dispatch(closeDialog()),
          sidebar: () => dispatch(closeSidebar()),
          page: () => dispatch(closePageView()),
        });
      }),
    [appDispatch, closeSidebar],
  );

  /** Переход по стеку до указанной вьюхи и указанной крошки во вьюху */
  const setStackToView = useCallback(
    (viewRequest: ViewParams, breadcrumbs: PageAreaBreadcrumbs, navType?: NavigationType) => {
      const targetViewKey = viewParamsToString(viewRequest);
      const sliceIdx = viewStack.findIndex(
        (view) => viewParamsToString(view.viewRequest) === targetViewKey,
      );
      if (sliceIdx === -1) throw new Error(`Unexpected page to redirect: ${targetViewKey}`);

      const prevView = viewStack[sliceIdx];
      const newView = { ...prevView, breadcrumbs };
      const newStack = [...viewStack].slice(0, sliceIdx).concat(newView);

      appDispatch(setupViewStack(newStack, { navigation: navType }));
    },

    [appDispatch, viewStack],
  );

  /** Инициализация стека при переходе по навигации **/
  const initializeStack = useCallback(
    (views: Array<FormViewParams>) => {
      appDispatch(setupViewStack(views));
    },
    [appDispatch],
  );

  const openWindow = useCallback(
    (viewParams: ViewParams, triggerRect?: DOMRect) => {
      appDispatch(
        openWindowView({
          viewParams,
          triggerRect: triggerRect
            ? {
                top: triggerRect.top,
                left: triggerRect.left,
                width: triggerRect.width,
                height: triggerRect.height,
              }
            : undefined,
        }),
      );
    },
    [appDispatch],
  );

  const closeWindow = useCallback(() => {
    appDispatch(closeWindowView());
  }, [appDispatch]);

  return {
    currentView,
    dialogs,
    sidebars,
    windowView,
    setDataRequest,
    initializeStack,
    setStackToView,
    openModalView,
    openSidebarView,
    setModalContext,
    openPageView,
    closeView,
    closeSidebar,
    openWindow,
    closeWindow,
  };
};

export default useViewController;
